import { useEffect, useMemo, useState } from 'react';
import { useAuthStore } from '@/store/auth/auth.store';
import {
  useFetchUsuarioResumen,
  useCancelarAprovisionamiento,
  useDesconfigurarAprovisionamiento,
} from '@/shared/actions/erp/tickets-soporte-cliente';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Divider,
  LinearProgress,
  Stack,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RouterIcon from '@mui/icons-material/Router';
import SettingsInputAntennaIcon from '@mui/icons-material/SettingsInputAntenna';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import HistoryIcon from '@mui/icons-material/History';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InventoryIcon from '@mui/icons-material/Inventory';
import PersonIcon from '@mui/icons-material/Person';
import ListAltIcon from '@mui/icons-material/ListAlt';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

import type {
  IVision360Data,
  IVision360Contrato,
  IVision360TrazabilidadItem,
  ITrazabilidadDetalleItem,
  IVision360Aprovisionamiento,
} from '@/shared/interfaces/erp/tickets-soporte-clientes';
import TicketDetailModal from '@/erp/tickets-clientes/detalle-ticket/components/TicketDetailModal';
import ElectricalServicesIcon from '@mui/icons-material/ElectricalServices';
import TabOrdenServicio from '../TabOrdenServicio/TabOrdenServicio';
import TabRecepcionEquipos from '../TabRecepcionEquipos/TabRecepcionEquipos';
import TabDocumentos from '../TabDocumentos/TabDocumentos';
import { Contrato } from '@/shared/interfaces';
type ActiveSection = 'datos_comerciales' | 'datos_tecnicos' | 'documentos' | 'aprovisionamiento' | 'tickets' | 'masivos' | 'financiero' | 'trazabilidad' | 'contratos' | 'orden_servicio' | 'recepcion_equipos';

// IMPORTANTE: el campo `perm` debe coincidir EXACTO con el codigo del
// permiso en la tabla api2_permiso. Algunos no son `vision360.ver_<key>`
// (ej. tickets → vision360.ver_tickets_cliente, masivos → vision360.ver_masivos_cliente)
// por eso no construimos el codigo a partir de la key.
const NAV_SECTIONS: { key: ActiveSection; label: string; icon: React.ReactNode; perm: string }[] = [
  { key: 'datos_comerciales', label: 'Datos Comerciales', icon: <StorefrontIcon fontSize="small" />,            perm: 'vision360.ver_datos_comerciales' },
  { key: 'datos_tecnicos',    label: 'Datos Técnicos',    icon: <RouterIcon fontSize="small" />,                perm: 'vision360.ver_datos_tecnicos' },
  { key: 'documentos',        label: 'Documentos',        icon: <DescriptionOutlinedIcon fontSize="small" />,   perm: 'vision360.ver_documentos' },
  { key: 'aprovisionamiento', label: 'Aprovisionamiento', icon: <SettingsInputAntennaIcon fontSize="small" />,  perm: 'vision360.ver_aprovisionamiento' },
  { key: 'tickets',           label: 'Tickets',           icon: <ConfirmationNumberIcon fontSize="small" />,    perm: 'vision360.ver_tickets_cliente' },
  { key: 'masivos',           label: 'Masivos',           icon: <WarningAmberIcon fontSize="small" />,          perm: 'vision360.ver_masivos_cliente' },
  { key: 'financiero',        label: 'Financiero',        icon: <AttachMoneyIcon fontSize="small" />,           perm: 'vision360.ver_financiero' },
  { key: 'trazabilidad',      label: 'Trazabilidad',      icon: <HistoryIcon fontSize="small" />,               perm: 'vision360.ver_trazabilidad' },
  { key: 'orden_servicio',    label: 'Orden Servicio',    icon: <ElectricalServicesIcon fontSize="small" />,    perm: 'vision360.ver_orden_servicio' },
  { key: 'recepcion_equipos', label: 'Retiro de Equipos', icon: <InventoryIcon fontSize="small" />,             perm: 'vision360.ver_recepcion_equipos' },
];

interface Vision360PanelProps {
  data: IVision360Data;
  selectedContrato: IVision360Contrato | null;
  onSelectContrato: (contrato: IVision360Contrato) => void;
  onCrearTicket: () => void;
  isLoading?: boolean;
}

export default function Vision360Panel({
  data,
  selectedContrato,
  onSelectContrato,
  onCrearTicket,
  isLoading,
}: Vision360PanelProps) {
  // Permisos por tab: cada tab del panel Vision 360 puede ocultarse via
  // permiso vision360.ver_<seccion>. Si el usuario no tiene NINGUN permiso
  // del prefijo "vision360." asumimos que el modulo todavia no fue
  // configurado y mostramos todas las tabs (fallback retrocompatible para
  // ambientes pre-migracion V1_12_0).
  const user = useAuthStore((s) => s.user);
  const isSuperUser = !!(user as any)?.is_superuser;
  const currentUserId = (user as any)?.user_id || (user as any)?.user || 0;
  const { data: resumenRes } = useFetchUsuarioResumen(currentUserId, !!currentUserId);
  const _r = resumenRes as any;
  const userPermisos: string[] =
    _r?.data?.data?.permisos || _r?.data?.permisos || _r?.permisos || [];
  const tieneAlgunVision360 = userPermisos.some((p) => p.startsWith('vision360.'));
  const can = (perm: string) =>
    isSuperUser || !tieneAlgunVision360 || userPermisos.includes(perm);

  const visibleSections = useMemo(
    () => NAV_SECTIONS.filter((s) =>  can(s.perm)),
    [userPermisos, isSuperUser, tieneAlgunVision360],
  );

  // Color del chip de estado de servicio. Tolerante a variantes que entrega
  // el backend ("ACTIVO", "Activo", "Servicio Activo", etc).
  // - Verde: cualquier string que contenga "activo" y NO "inactivo".
  // - Rojo: cualquier variante de "inactivo", "suspendido", "cortado".
  // - Default gris: cualquier otra cosa o vacio.
  const estadoServicioColor = (raw?: string | null): 'success' | 'error' | 'default' => {
    if (!raw) return 'default';
    const s = raw.toLowerCase();
    if (s.includes('inactivo') || s.includes('suspendido') || s.includes('cortado')) return 'error';
    if (s.includes('activo')) return 'success';
    return 'default';
  };

  const [activeSection, setActiveSection] = useState<ActiveSection>(
    (visibleSections[0]?.key as ActiveSection) ?? 'datos_comerciales',
  );
  const [openTicketModal, setOpenTicketModal] = useState<string | null>(null);
  // Evento de trazabilidad cuyo desglose campo-a-campo se muestra en el modal
  // "Mas detalles" (p.ej. una activacion de aprovisionamiento).
  const [detalleTraza, setDetalleTraza] = useState<IVision360TrazabilidadItem | null>(null);
  // Confirmacion de baja (cancelar / desconfigurar) del aprovisionamiento.
  const cancelarMut = useCancelarAprovisionamiento();
  const desconfigurarMut = useDesconfigurarAprovisionamiento();
  const [accionBaja, setAccionBaja] = useState<
    { modo: 'cancelar' | 'desconfigurar'; aprov: IVision360Aprovisionamiento } | null
  >(null);
  const ejecutandoBaja = cancelarMut.isPending || desconfigurarMut.isPending;

  // Si el tab activo deja de ser visible (perdio permiso o cambio el rol)
  // saltamos al primero disponible.
  //
  // EXCEPCION: 'contratos' es una vista especial que NO esta en
  // NAV_SECTIONS porque no se accede desde la barra de tabs sino desde
  // el boton "(#) Contratos" del header del cliente. No la incluimos en
  // la barra para no duplicar la UI, pero es un destino valido — sin
  // este guard, el useEffect la "expulsaba" por no encontrarla en
  // visibleSections.
  useEffect(() => {
    if (activeSection === 'contratos') return;
    if (!visibleSections.find((s) => s.key === activeSection) && visibleSections[0]) {
      setActiveSection(visibleSections[0].key as ActiveSection);
    }
  }, [visibleSections, activeSection]);

  const { cliente, contratos, tickets, financiero, masivos_activos, trazabilidad } = data;
  const aprovisionamiento = data.aprovisionamiento ?? [];

  // Un cliente puede tener varios contratos; cada aprovisionamiento es sobre UN
  // contrato. La vista se controla por el selector de contratos: mostramos solo
  // el aprovisionamiento del contrato seleccionado.
  const aprovDelContrato = selectedContrato
    ? aprovisionamiento.filter((a) => a.contrato_codigo === selectedContrato.codigo)
    : aprovisionamiento;
  const aprovContratoSel = aprovDelContrato[0] ?? null;
  // Permiso para Cancelar/Desconfigurar (separado del de solo ver la pestaña).
  const puedeGestionarAprov = can('vision360.gestionar_aprovisionamiento');

  // Estado de servicio a mostrar en el tag superior, derivado del aprovisionamiento:
  //  - bloqueado (cancelado)   -> "Equipos retirados"
  //  - registro vigente activo -> "Servicio activo"
  //  - sin aprovisionamiento   -> el estado del contrato (fallback)
  const estadoServicioHeader = aprovContratoSel
    ? (aprovContratoSel.bloqueado ? 'Equipos retirados' : 'Servicio activo')
    : (selectedContrato?.estado_servicio || selectedContrato?.estado_contrato);

  const confirmarBaja = () => {
    if (!accionBaja) return;
    const params = {
      identificacion: accionBaja.aprov.identificacion || cliente.identificacion,
      contrato: accionBaja.aprov.contrato_codigo,
    };
    const mut = accionBaja.modo === 'cancelar' ? cancelarMut : desconfigurarMut;
    mut.mutate(params as any, { onSettled: () => setAccionBaja(null) });
  };

  // Extrae el codigo de contrato de un item de trazabilidad. Prefiere el campo
  // explicito contrato_codigo; si no llega (registros viejos), lo intenta sacar
  // de categoria_cambio ("Contrato SN-XXX" / "Aprovisionamiento SN-XXX").
  const contratoDeTraza = (t: IVision360TrazabilidadItem): string => {
    if (t.contrato_codigo) return t.contrato_codigo;
    const m = t.categoria_cambio?.match(/(SN-[A-Za-z0-9_-]+)/);
    if (m) return m[1];
    // Cambios a nivel cliente (integracion ACTUALIZACION_API): categoria "Cliente".
    if ((t.categoria_cambio ?? '').toLowerCase() === 'cliente') return 'Cliente';
    return '—';
  };

  // Etiqueta y color amigables por tipo de cambio. IMPORTANTE: APROVISIONAMIENTO
  // se mantiene EXACTO como en prod (no se toca); solo se adaptan los demas.
  const labelTipoCambio = (tipo?: string): string => {
    switch (tipo) {
      case 'APROVISIONAMIENTO': return 'Aprovisionamiento';
      case 'ACTUALIZACION_API': return 'Actualización (API)';
      case 'CANCELACION': return 'Cancelación';
      case 'DESCONFIGURACION': return 'Desconfiguración';
      default: return tipo || '—';
    }
  };
  const colorTipoCambio = (
    tipo?: string,
  ): 'info' | 'success' | 'error' | 'warning' | 'default' => {
    switch (tipo) {
      case 'APROVISIONAMIENTO': return 'info';
      case 'ACTUALIZACION_API': return 'success';
      case 'CANCELACION': return 'error';
      case 'DESCONFIGURACION': return 'warning';
      default: return 'default';
    }
  };

  // Parsea el JSON de `detalle` a una lista de cambios campo-a-campo.
  const parseDetalle = (raw?: string | null): ITrazabilidadDetalleItem[] => {
    if (!raw) return [];
    try {
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? (arr as ITrazabilidadDetalleItem[]) : [];
    } catch {
      return [];
    }
  };

  // Detalle a mostrar en "Más detalles" para CUALQUIER fila de trazabilidad:
  //  - Si la fila trae el JSON `detalle` (eventos agrupados como
  //    APROVISIONAMIENTO), se usa ese desglose completo.
  //  - Si no (ACTUALIZACION_API y demás cambios por-campo), se arma una fila
  //    con el propio campo_modificado / valor_anterior / valor_nuevo.
  const getDetalleRows = (t: IVision360TrazabilidadItem): ITrazabilidadDetalleItem[] => {
    const parsed = parseDetalle(t.detalle);
    if (parsed.length > 0) return parsed;
    if (t.campo_modificado) {
      return [{
        campo: t.campo_modificado,
        anterior: t.valor_anterior ?? null,
        nuevo: t.valor_nuevo ?? null,
      }];
    }
    return [];
  };

  // Auto-seleccionar primer contrato si no hay uno seleccionado
  useEffect(() => {
    if (!selectedContrato && contratos.length > 0) {
      onSelectContrato(contratos[0]);
    }
  }, [selectedContrato, contratos, onSelectContrato]);

  // Cuando contratos se refresca (tras finalizar retiro), sincronizar
  // selectedContrato con el objeto nuevo para reflejar estado_servicio
  useEffect(() => {
    if (selectedContrato && contratos.length > 0) {
      const updated = contratos.find(c => c.id_contrato === selectedContrato.id_contrato);
      if (updated && updated !== selectedContrato) {
        onSelectContrato(updated);
      }
    }
  }, [contratos, selectedContrato, onSelectContrato]);

  if (isLoading) return <LinearProgress sx={{ mt: 2 }} />;

  const otrosContratos = contratos.filter(
    (c) => c.id_contrato !== selectedContrato?.id_contrato,
  );

  return (
    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* 1. Alerta masivo */}
      {masivos_activos.length > 0 && (
        <Alert severity="error" icon={<WarningAmberIcon />}>
          <strong>MASIVO ACTIVO:</strong>{' '}
          {masivos_activos.map((m) => (
            <span key={m.codigo}>
              {m.codigo} - {m.descripcion} ({m.clientes_afectados_count} clientes)
            </span>
          ))}
        </Alert>
      )}

      {/* 2. Header cliente
          Si el contrato seleccionado tiene su propia razon_social (nombre_cliente_contrato)
          distinta a la del cliente principal — caso de una cedula con varios contratos
          con nombres diferentes (ej. ASSISNET vs MEGATEL bajo el mismo RUC) — mostramos
          el nombre del contrato como titulo principal y el del cliente como subtitulo.
          Si coinciden o no hay nombre por contrato, mostramos solo el del cliente. */}
      <Paper sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <PersonIcon />
              <Box>
                {(() => {
                  const nombreContrato = (selectedContrato as any)?.nombre_cliente_contrato as string | undefined;
                  const muestraNombreContrato =
                    !!nombreContrato &&
                    nombreContrato.trim() !== '' &&
                    nombreContrato.trim() !== (cliente.razon_social || '').trim();
                  return (
                    <>
                      <Typography variant="h6" fontWeight={700}>
                        {muestraNombreContrato ? nombreContrato : cliente.razon_social}
                      </Typography>
                      {muestraNombreContrato && (
                        <Typography variant="caption" sx={{ opacity: 0.85, display: 'block' }}>
                          RUC: {cliente.razon_social}
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        {cliente.tipo_identificacion}: {cliente.identificacion} | {cliente.ciudad}
                        {cliente.provincia ? `, ${cliente.provincia}` : ''}
                      </Typography>
                    </>
                  );
                })()}
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} md={7}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: 'nowrap' }}>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center" justifyContent="flex-end" sx={{ flex: 1, minWidth: 0 }}>
                {cliente.celular && (
                  <Chip label={`Tel: ${cliente.celular}`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
                )}
                {cliente.celular_2 && (
                  <Chip label={`Tel2: ${cliente.celular_2}`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
                )}
                {cliente.email && (
                  <Chip label={cliente.email} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
                )}
                {cliente.es_tercera_edad && <Chip label="3ra Edad" size="small" color="warning" />}
                {cliente.es_discapacitado && <Chip label="Discapacidad" size="small" color="warning" />}
              </Stack>
              <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                {contratos.length > 1 && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ListAltIcon />}
                    onClick={() => setActiveSection('contratos')}
                    sx={{
                      color: 'white',
                      borderColor: 'rgba(255,255,255,0.5)',
                      whiteSpace: 'nowrap',
                      '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                    }}
                  >
                    Contratos ({contratos.length})
                  </Button>
                )}
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={onCrearTicket}
                  sx={{
                    bgcolor: 'white',
                    color: 'primary.main',
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.85)' },
                  }}
                >
                  Crear Ticket
                </Button>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* 3. Info Tags: Contrato, Tipo Contrato, IP, Estado Servicio, Saldo Pendiente */}
      {selectedContrato && (
        <Grid container spacing={1.5}>
          {[
            {
              label: 'Contrato',
              value: selectedContrato.codigo,
              color: 'primary.main',
              // Referencia del contrato de EVA (agendamiento): subtítulo bajo el código.
              subtitle: selectedContrato.contrato_eva || undefined,
            },
            {
              // Tag de tipo de contrato (HOME / PYME / CORPORATIVO). Mapea
              // al campo tipo_plan del contrato. Si esta vacio mostramos '-'
              // para no romper el layout.
              label: 'Tipo Contrato',
              value: (selectedContrato.tipo_plan || '-').toString().toUpperCase(),
              color:
                selectedContrato.tipo_plan === 'CORPORATIVO'
                  ? 'warning.main'
                  : selectedContrato.tipo_plan === 'PYME'
                  ? 'info.main'
                  : 'text.primary',
            },
            {
              label: 'IP',
              value: selectedContrato.ipv4_actual || 'N/A',
              color: 'text.primary',
              isIp: true,
            },
            {
              label: 'Estado Servicio',
              // Derivado del aprovisionamiento del contrato: cancelado -> "Equipos
              // retirados", activo -> "Servicio activo"; si no hay, el del contrato.
              value: estadoServicioHeader,
              color: (() => {
                if (aprovContratoSel?.bloqueado) return 'error.main';       // Equipos retirados
                if (aprovContratoSel) return 'success.main';                 // Servicio activo
                const c = estadoServicioColor(
                  selectedContrato.estado_servicio || selectedContrato.estado_contrato,
                );
                return c === 'success' ? 'success.main'
                  : c === 'error' ? 'error.main'
                  : 'text.secondary';
              })(),
            },
            {
              label: 'Saldo Pendiente',
              value: `$${financiero.total_pendiente}`,
              color: parseFloat(financiero.total_pendiente) > 0 ? 'error.main' : 'success.main',
            },
          ].map((tag) => (
            <Grid item xs={6} sm key={tag.label}>
              <Card variant="outlined" sx={{ transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 } }}>
                <CardContent sx={{ textAlign: 'center', py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                  {'isIp' in tag && tag.isIp && tag.value && tag.value !== 'N/A' ? (
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      color={tag.color}
                      noWrap
                      component="a"
                      href={`http://${tag.value}:80`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                    >
                      {tag.value}
                    </Typography>
                  ) : (
                    <Typography variant="h5" fontWeight="bold" color={tag.color} noWrap>
                      {tag.value}
                    </Typography>
                  )}
                  {'subtitle' in tag && tag.subtitle && (
                    <Typography variant="caption" display="block" color="text.secondary" noWrap title={`Contrato EVA: ${tag.subtitle}`}>
                      EVA: {tag.subtitle}
                    </Typography>
                  )}
                  <Typography variant="caption" display="block" fontWeight={500} color="text.secondary">
                    {tag.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* 4. Layout 2 columnas: navegación izquierda, detalle derecha */}
      <Grid container spacing={2}>
        {/* Columna izquierda — Navegación por secciones */}
        <Grid item xs={12} md={2.5}>
          <Stack spacing={1}>
            {visibleSections.map((section) => {
              const isActive = activeSection === section.key;
              return (
                <Card
                  key={section.key}
                  variant="outlined"
                  onClick={() => setActiveSection(section.key)}
                  sx={{
                    cursor: 'pointer',
                    border: isActive ? '2px solid' : '1px solid',
                    borderColor: isActive ? 'primary.main' : 'divider',
                    bgcolor: isActive ? 'primary.50' : 'background.paper',
                    transition: 'all 0.2s',
                    '&:hover': { borderColor: 'primary.main' },
                  }}
                >
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ color: isActive ? 'primary.main' : 'text.secondary' }}>
                        {section.icon}
                      </Box>
                      <Typography
                        variant="body2"
                        fontWeight={isActive ? 700 : 500}
                        color={isActive ? 'primary.main' : 'text.primary'}
                      >
                        {section.label}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        </Grid>

        {/* Columna derecha — Detalle sección activa */}
        <Grid item xs={12} md={9.5}>
          <Box sx={{ overflowY: 'auto', maxHeight: { md: 'calc(100vh - 300px)' } }}>

            {/* Datos Comerciales */}
            {activeSection === 'datos_comerciales' && selectedContrato && (
              <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <StorefrontIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle1" fontWeight={700}>Datos Comerciales</Typography>
                </Stack>

                {/* Servicio principal: Internet */}
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                      <Typography variant="subtitle2" fontWeight={700} color="primary.main">
                        Internet
                      </Typography>
                      <Chip
                        label={selectedContrato.estado_servicio || selectedContrato.estado_contrato || 'N/A'}
                        size="small"
                        color={estadoServicioColor(
                          selectedContrato.estado_servicio || selectedContrato.estado_contrato,
                        )}
                        sx={{ fontWeight: 600 }}
                      />
                    </Stack>
                    <Grid container spacing={2}>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>Plan</Typography>
                        <Typography variant="body2" fontWeight={600}>{selectedContrato.plan || 'N/A'}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>Velocidad</Typography>
                        <Typography variant="body2" fontWeight={600}>{selectedContrato.velocidad ? `${selectedContrato.velocidad} Mbps` : 'N/A'}</Typography>
                      </Grid>
                      {/* Bonificacion: solo visible cuando el plan es
                          CORPORATIVO. Valor viene de Softnet (clave
                          velocidad_bonificacion) y se persiste en el
                          contrato. Si esta vacio mostramos '-'. */}
                      {selectedContrato.tipo_plan === 'CORPORATIVO' && (
                        <Grid item xs={6} md={3}>
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>Bonificación</Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {(selectedContrato as any).velocidad_bonificacion
                              ? `${(selectedContrato as any).velocidad_bonificacion} Mbps`
                              : '-'}
                          </Typography>
                        </Grid>
                      )}
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>Costo Mensual</Typography>
                        <Typography variant="body2" fontWeight={600}>{selectedContrato.valor_plan ? `$${selectedContrato.valor_plan}` : 'N/A'}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>Tipo Servicio</Typography>
                        <Typography variant="body2" fontWeight={600}>{selectedContrato.tipo_servicio || 'N/A'}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>Tipo Plan</Typography>
                        <Typography variant="body2" fontWeight={600}>{selectedContrato.tipo_plan || 'N/A'}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>Contrato</Typography>
                        <Typography variant="body2" fontWeight={600}>{selectedContrato.codigo || 'N/A'}</Typography>
                      </Grid>
                      {/* Placa: contrato.tag de Softnet. Va al lado del
                          contrato porque identifica al mismo contrato en los
                          sistemas de red. Los contratos importados antes de
                          que existiera la columna la llenan al sincronizar. */}
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>Placa</Typography>
                        <Typography variant="body2" fontWeight={600}>{selectedContrato.placa || '-'}</Typography>
                      </Grid>
                      {selectedContrato.fecha_inicio_contrato && (
                        <Grid item xs={6} md={3}>
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>Fecha Inicio</Typography>
                          <Typography variant="body2" fontWeight={600}>{selectedContrato.fecha_inicio_contrato}</Typography>
                        </Grid>
                      )}
                      {/* Contrato empresa: deriva de contratoAprobado de Softnet.
                          "1" -> Gedatecu S.A | "0" -> Eliana Morocho */}
                      {(() => {
                        const ca = String((selectedContrato as any).contrato_aprobado ?? '').trim();
                        if (ca === '') return null;
                        const empresa = ca === '1' ? 'Gedatecu S.A' : 'Eliana Morocho';
                        return (
                          <Grid item xs={6} md={3}>
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>Contrato empresa</Typography>
                            <Typography variant="body2" fontWeight={600}>{empresa}</Typography>
                          </Grid>
                        );
                      })()}
                      {selectedContrato.direccion && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>Dirección Servicio</Typography>
                          <Typography variant="body2" fontWeight={500}>{selectedContrato.direccion}</Typography>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>

                {/* Equipo Comercial: canal de venta, asesor, supervisor + */}
                {/* nuevos campos V1_16_0 (departamento + id_empleado del vendedor). */}
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="subtitle2" fontWeight={700} color="primary.main" sx={{ mb: 1.5 }}>
                      Equipo Comercial
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} md={4}>
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>Canal de Venta</Typography>
                        <Typography variant="body2" fontWeight={600}>{selectedContrato.canal_venta || '-'}</Typography>
                      </Grid>
                      <Grid item xs={6} md={4}>
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>Asesor</Typography>
                        <Typography variant="body2" fontWeight={600}>{selectedContrato.asesor || '-'}</Typography>
                      </Grid>
                      <Grid item xs={6} md={4}>
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>Supervisor</Typography>
                        <Typography variant="body2" fontWeight={600}>{selectedContrato.supervisor || '-'}</Typography>
                      </Grid>
                      <Grid item xs={6} md={4}>
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>Departamento Ventas</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {(selectedContrato as any).departamento_ventas
                            || (selectedContrato as any).equipo_comercial?.departamento_ventas
                            || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={4}>
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>ID Empleado</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {(selectedContrato as any).id_empleado_venta
                            || (selectedContrato as any).equipo_comercial?.id_empleado_venta
                            || '-'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Promociones y Forma de Pago */}
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="subtitle2" fontWeight={700} color="primary.main" sx={{ mb: 1.5 }}>
                      Promociones y Pago
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>Promociones</Typography>
                        {selectedContrato.promociones ? (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {selectedContrato.promociones.split(',').map((p, i) => (
                              <Chip key={i} label={p.trim()} size="small" variant="outlined" color="primary" />
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="body2" fontWeight={500}>-</Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>Forma de Pago</Typography>
                        <Typography variant="body2" fontWeight={600}>{selectedContrato.forma_pago || '-'}</Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Placeholder para servicios adicionales futuros */}
                <Alert severity="info" variant="outlined" sx={{ borderStyle: 'dashed' }}>
                  Servicios adicionales (TV, telefonia, etc.) se mostrarán aquí cuando estén disponibles.
                </Alert>
              </Paper>
            )}

            {/* Datos Técnicos */}
            {activeSection === 'datos_tecnicos' && selectedContrato && (
              <Stack spacing={2}>
                {/* Bloque 1: Datos tecnicos basicos del contrato (campos del modelo) */}
                <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                    <RouterIcon color="primary" fontSize="small" />
                    <Typography variant="subtitle1" fontWeight={700}>Datos Técnicos</Typography>
                  </Stack>
                  <Grid container spacing={2}>
                    {(() => {
                      // Bloque opcional datos_tecnicos (Softnet detallado) — solo
                      // se usa para enriquecer campos puntuales; los basicos siguen
                      // viniendo del contrato.
                      const dt = selectedContrato.datos_tecnicos || {};

                      // Rango operativo de potencia optica aceptable: [-22, -19] dBm.
                      // Fuera de ese rango es alarma (cliente muy lejos/atenuado o
                      // demasiado cerca/quemando el receptor). Se pinta en rojo.
                      const potenciaOutOfRange = (raw: any): boolean => {
                        if (raw === null || raw === undefined || raw === '') return false;
                        const n = parseFloat(String(raw));
                        if (!isFinite(n)) return false;
                        return n < -22 || n > -19;
                      };
                      const potenciaAlarma = potenciaOutOfRange(dt.potencia_dbm);

                      const items: Array<{ label: string; value?: string | null; isIp?: boolean; isStatus?: boolean; isAlarm?: boolean }> = [
                        { label: 'Estado Servicio', value: selectedContrato.estado_servicio || selectedContrato.estado_contrato, isStatus: true },
                        { label: 'NAP', value: selectedContrato.nap_nombre },
                        { label: 'OLT', value: dt.olt || `${selectedContrato.olt_nombre || ''}${selectedContrato.olt_ip ? ` (${selectedContrato.olt_ip})` : ''}` },
                        // Puerto NAP especifico (V1_16_0). Fallback al campo
                        // generico "puerto" para contratos legacy.
                        { label: 'Puerto NAP', value: (dt as any).puerto_nap || selectedContrato.puerto },
                        { label: 'IP (IPv4)', value: dt.ipv4 || selectedContrato.ipv4_actual, isIp: true },
                        { label: 'IPv4 Pública', value: dt.ipv4_publica, isIp: true },
                        { label: 'Radio Base', value: dt.radio_base },
                        { label: 'PPPoE', value: selectedContrato.pppoe },
                        { label: 'Usuario PPPoE', value: dt.usuario_pppoe },
                        { label: 'Clave PPPoE', value: dt.clave_pppoe },
                        { label: 'Potencia (dBm)', value: dt.potencia_dbm, isAlarm: potenciaAlarma },
                        { label: 'Distancia (m)', value: dt.distancia },
                        // Cliente BRAS: flag NO/SI agregado en V1_16_0.
                        { label: 'Cliente BRAS', value: (dt as any).cliente_bras },
                        { label: 'Huawei Domain previo', value: dt.previous_huawei_domain || '-' },
                        // tecnico_instalador puede venir tanto en dt como
                        // en el contrato plano (ambos se mapean en backend).
                        { label: 'Técnico Instalador', value: (dt as any).tecnico_instalador || selectedContrato.tecnico_instalador || '-' },
                      ];
                      return items.map((item) => (
                        <Grid item xs={6} md={4} key={item.label}>
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1 }}>{item.label}</Typography>
                          {item.isIp && item.value && item.value !== '-' ? (
                            <Typography
                              variant="body2"
                              fontWeight={500}
                              component="a"
                              href={`http://${item.value}:80`}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                            >
                              {item.value}
                            </Typography>
                          ) : item.isStatus ? (
                            // Estado Servicio: aqui se muestra en negro como texto
                            // informativo (el indicador rojo/verde ya vive en el
                            // chip de la cabecera del contrato).
                            <Typography variant="body2" fontWeight={700}>
                              {item.value || 'N/A'}
                            </Typography>
                          ) : item.isAlarm ? (
                            <Typography variant="body2" fontWeight={700} color="error.main">
                              {item.value || 'N/A'}
                            </Typography>
                          ) : (
                            <Typography variant="body2" fontWeight={500}>{item.value || 'N/A'}</Typography>
                          )}
                        </Grid>
                      ));
                    })()}
                  </Grid>
                </Paper>

                {/* Bloque 2: Equipos del contrato (lista variable) */}
                <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                    <RouterIcon color="primary" fontSize="small" />
                    <Typography variant="subtitle1" fontWeight={700}>
                      Equipos ({(selectedContrato.equipos || []).length})
                    </Typography>
                  </Stack>
                  {(selectedContrato.equipos || []).length === 0 ? (
                    <Alert severity="info" variant="outlined">Sin equipos asignados al contrato.</Alert>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Marca / Modelo</TableCell>
                            <TableCell>Serie MAC</TableCell>
                            <TableCell>Serie Num</TableCell>
                            <TableCell align="right">Cantidad</TableCell>
                            <TableCell>Observaciones</TableCell>
                            <TableCell>Fecha</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(selectedContrato.equipos || []).map((e, idx) => (
                            <TableRow key={`${e.id_detalle_contrato ?? idx}`} hover>
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>
                                  {e.modelo || '-'}
                                </Typography>
                                {e.marca && (
                                  <Typography variant="caption" color="text.secondary">{e.marca}</Typography>
                                )}
                              </TableCell>
                              <TableCell sx={{ fontFamily: 'monospace' }}>{e.serie_mac || '-'}</TableCell>
                              <TableCell sx={{ fontFamily: 'monospace' }}>{e.serie_num || '-'}</TableCell>
                              <TableCell align="right">{e.cantidad ?? '-'}</TableCell>
                              <TableCell>{e.observaciones || '-'}</TableCell>
                              <TableCell>{e.fecha_sistema || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Paper>

                {/* Bloque 3: Materiales del contrato */}
                <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                    <RouterIcon color="primary" fontSize="small" />
                    <Typography variant="subtitle1" fontWeight={700}>
                      Materiales ({(selectedContrato.materiales || []).length})
                    </Typography>
                  </Stack>
                  {(selectedContrato.materiales || []).length === 0 ? (
                    <Alert severity="info" variant="outlined">Sin materiales registrados.</Alert>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Tipo</TableCell>
                            <TableCell>Material</TableCell>
                            <TableCell>Código</TableCell>
                            <TableCell align="right">Cantidad</TableCell>
                            <TableCell align="right">Costo</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(selectedContrato.materiales || []).map((m, idx) => (
                            <TableRow key={`${m.id ?? idx}`} hover>
                              <TableCell>
                                <Chip
                                  label={(m.tipo || '-').toUpperCase()}
                                  size="small"
                                  variant="outlined"
                                  color={m.tipo === 'instalacion' ? 'primary' : 'default'}
                                />
                              </TableCell>
                              <TableCell>{m.tie_nombre || '-'}</TableCell>
                              <TableCell sx={{ fontFamily: 'monospace' }}>{m.codigo || '-'}</TableCell>
                              <TableCell align="right">{m.cantidad ?? '-'}</TableCell>
                              <TableCell align="right">{m.costo ? `$${m.costo}` : '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Paper>
              </Stack>
            )}

            {/* Documentos */}
            {activeSection === 'documentos' && selectedContrato && (
              <TabDocumentos key={selectedContrato.id_contrato} contrato={selectedContrato} can={can} />
            )}


            {/* Tickets */}
            {activeSection === 'tickets' && (
              <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <ConfirmationNumberIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle1" fontWeight={700}>Tickets ({tickets.length})</Typography>
                </Stack>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Código</TableCell>
                        <TableCell>Tipo</TableCell>
                        <TableCell>Estado</TableCell>
                        <TableCell>Prioridad</TableCell>
                        <TableCell>Área</TableCell>
                        <TableCell>Fecha</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tickets.map((t) => (
                        <TableRow key={t.id_ticket} hover>
                          <TableCell>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              component="a"
                              onClick={(e) => { e.preventDefault(); setOpenTicketModal(t.id_ticket); }}
                              sx={{
                                color: 'primary.main',
                                cursor: 'pointer',
                                textDecoration: 'none',
                                '&:hover': { textDecoration: 'underline' },
                              }}
                            >
                              {t.codigo_ticket}
                            </Typography>
                          </TableCell>
                          <TableCell>{t.tipo}</TableCell>
                          <TableCell>
                            <Chip
                              label={t.estado}
                              size="small"
                              sx={{ bgcolor: t.estado_color || '#e0e0e0', color: 'white', fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={t.prioridad}
                              size="small"
                              sx={{ bgcolor: t.prioridad_color || '#9e9e9e', color: 'white' }}
                            />
                          </TableCell>
                          <TableCell>{t.area_destino}</TableCell>
                          <TableCell>{new Date(t.fecha_creacion).toLocaleDateString('es-EC')}</TableCell>
                        </TableRow>
                      ))}
                      {tickets.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography color="text.secondary">Sin tickets registrados</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {/* Masivos */}
            {activeSection === 'masivos' && (
              <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <WarningAmberIcon color={masivos_activos.length > 0 ? 'error' : 'disabled'} fontSize="small" />
                  <Typography variant="subtitle1" fontWeight={700}>
                    Masivos Activos ({masivos_activos.length})
                  </Typography>
                </Stack>
                {masivos_activos.length === 0 ? (
                  <Alert severity="success" variant="outlined">
                    Este cliente no esta afectado por ningun masivo activo.
                  </Alert>
                ) : (
                  <Stack spacing={2}>
                    {masivos_activos.map((m) => {
                      // Union lista nueva + singular legacy para mostrar todos
                      // los elementos afectados en cada nivel (soporta masivos
                      // antiguos con un solo elemento y nuevos multi-seleccion).
                      const dedup = (arr: (string | null | undefined)[]): string[] =>
                        arr.filter((v, i, a) => !!v && a.indexOf(v) === i) as string[];
                      const naps = dedup([...(m.naps_afectadas || [])]);
                      const troncales = dedup([
                        ...(m.troncales_afectadas || []),
                        m.troncal_afectada,
                      ]);
                      const olts = dedup([
                        ...(m.olts_afectadas || []),
                        m.olt_afectada,
                      ]);
                      const routers = dedup([
                        ...(m.routers_afectados || []),
                        m.router_afectado,
                      ]);
                      // Color/tag cambia segun estado: pausado = warning (amarillo),
                      // activo = error (rojo). El frontend de nuevo-ticket usa
                      // esto para mostrar "ALERTA" en vez de "MASIVO".
                      const isPausado = m.estado_masivo === 'pausado';
                      const borderColor = isPausado ? 'warning.light' : 'error.light';
                      const headerColor = isPausado ? 'warning.main' : 'error.main';
                      const estadoChipColor: 'warning' | 'error' = isPausado ? 'warning' : 'error';
                      const estadoChipLabel = isPausado ? 'PAUSADO / ALERTA' : 'MASIVO ACTIVO';
                      return (
                        <Card key={m.codigo} variant="outlined" sx={{ borderColor }}>
                          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                              <Typography variant="subtitle2" fontWeight={700} color={headerColor}>
                                {m.codigo}
                              </Typography>
                              <Stack direction="row" spacing={1}>
                                <Chip
                                  label={estadoChipLabel}
                                  size="small"
                                  color={estadoChipColor}
                                />
                                <Chip
                                  label={(m.nivel_afectacion || '').toUpperCase() || 'N/A'}
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                />
                                <Chip
                                  label={`${m.clientes_afectados_count} clientes`}
                                  size="small"
                                  color={estadoChipColor}
                                  variant="outlined"
                                />
                              </Stack>
                            </Stack>
                            <Divider sx={{ my: 1 }} />
                            <Grid container spacing={1.5}>
                              <Grid item xs={6} md={3}>
                                <Typography variant="caption" color="text.secondary">OLT(s)</Typography>
                                <Typography variant="body2" fontWeight={500}>
                                  {olts.length > 0 ? olts.join(', ') : '-'}
                                </Typography>
                              </Grid>
                              <Grid item xs={6} md={3}>
                                <Typography variant="caption" color="text.secondary">Troncal(es)</Typography>
                                <Typography variant="body2" fontWeight={500}>
                                  {troncales.length > 0 ? troncales.join(', ') : '-'}
                                </Typography>
                              </Grid>
                              <Grid item xs={6} md={3}>
                                <Typography variant="caption" color="text.secondary">NAP(s)</Typography>
                                <Typography variant="body2" fontWeight={500}>
                                  {naps.length > 0 ? naps.join(', ') : '-'}
                                </Typography>
                              </Grid>
                              <Grid item xs={6} md={3}>
                                <Typography variant="caption" color="text.secondary">Router(s)</Typography>
                                <Typography variant="body2" fontWeight={500}>
                                  {routers.length > 0 ? routers.join(', ') : '-'}
                                </Typography>
                              </Grid>
                              <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary">Descripcion</Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                  {m.descripcion || '-'}
                                </Typography>
                              </Grid>
                              {isPausado && m.motivo_pausa && (
                                <Grid item xs={12}>
                                  <Typography variant="caption" color="text.secondary">
                                    Motivo de pausa
                                  </Typography>
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }} color="warning.main">
                                    {m.motivo_pausa}
                                  </Typography>
                                </Grid>
                              )}
                              {m.fecha_creacion && (
                                <Grid item xs={12}>
                                  <Typography variant="caption" color="text.secondary">
                                    Reportado: {new Date(m.fecha_creacion).toLocaleString('es-EC')}
                                  </Typography>
                                </Grid>
                              )}
                            </Grid>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Stack>
                )}
              </Paper>
            )}

            {/* Financiero */}
            {activeSection === 'financiero' && (
              <Stack spacing={2}>
                {/* Tarjetas resumen */}
                <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                    <AttachMoneyIcon color="primary" fontSize="small" />
                    <Typography variant="subtitle1" fontWeight={700}>Financiero ({financiero.cantidad_facturas})</Typography>
                  </Stack>
                  <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                    <Card variant="outlined" sx={{ flex: 1, minWidth: 140 }}>
                      <CardContent sx={{ textAlign: 'center', py: 1, '&:last-child': { pb: 1 } }}>
                        <Typography variant="caption" color="text.secondary">Saldo Pendiente</Typography>
                        <Typography variant="h5" color={parseFloat(financiero.total_pendiente) > 0 ? 'error.main' : 'success.main'} fontWeight={700}>
                          ${financiero.total_pendiente}
                        </Typography>
                      </CardContent>
                    </Card>
                    {financiero.fibra_points?.puntos_disponibles !== undefined && (
                      <Card variant="outlined" sx={{ flex: 1, minWidth: 140 }}>
                        <CardContent sx={{ textAlign: 'center', py: 1, '&:last-child': { pb: 1 } }}>
                          <Typography variant="caption" color="text.secondary">Fibra Points</Typography>
                          <Typography variant="h5" fontWeight={700} color="primary.main">
                            {financiero.fibra_points.puntos_disponibles}
                          </Typography>
                        </CardContent>
                      </Card>
                    )}
                  </Stack>

                  {/* Facturas recientes */}
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Facturas recientes</Typography>
                  <TableContainer sx={{ mb: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Código</TableCell>
                          <TableCell>Fecha Emisión</TableCell>
                          <TableCell>Total</TableCell>
                          <TableCell>Secuencial</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {financiero.facturas_recientes.map((f) => (
                          <TableRow key={f.codigo} hover>
                            <TableCell>{f.codigo}</TableCell>
                            <TableCell>{f.fecha_emision}</TableCell>
                            <TableCell>${f.total}</TableCell>
                            <TableCell>{f.secuencial}</TableCell>
                          </TableRow>
                        ))}
                        {financiero.facturas_recientes.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              <Typography color="text.secondary">Sin facturas</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>

                {/* Pagos (Softnet) */}
                <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                    <AttachMoneyIcon color="success" fontSize="small" />
                    <Typography variant="subtitle1" fontWeight={700}>
                      Pagos ({(financiero.pagos || []).length})
                    </Typography>
                  </Stack>
                  {(financiero.pagos || []).length === 0 ? (
                    <Alert severity="info" variant="outlined">Sin pagos registrados.</Alert>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Período</TableCell>
                            <TableCell>Concepto</TableCell>
                            <TableCell align="right">A pagar</TableCell>
                            <TableCell align="right">Pagado</TableCell>
                            <TableCell>Documento</TableCell>
                            <TableCell>Fecha pago</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(financiero.pagos || []).map((p, idx) => (
                            <TableRow key={p.idValorGenerado || idx} hover>
                              <TableCell>{p.idMes && p.anio ? `${p.idMes}/${p.anio}` : '-'}</TableCell>
                              <TableCell>{p.observaciones || '-'}</TableCell>
                              <TableCell align="right">${p.totalAPagar ?? '-'}</TableCell>
                              <TableCell align="right">${p.totalPagado ?? '-'}</TableCell>
                              <TableCell sx={{ fontFamily: 'monospace' }}>{p.numeroDocumento || '-'}</TableCell>
                              <TableCell>{p.fechaPago || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Paper>

                {/* Deudas */}
                <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                    <WarningAmberIcon color={(financiero.deudas || []).length > 0 ? 'error' : 'disabled'} fontSize="small" />
                    <Typography variant="subtitle1" fontWeight={700}>
                      Deudas ({(financiero.deudas || []).length})
                    </Typography>
                  </Stack>
                  {(financiero.deudas || []).length === 0 ? (
                    <Alert severity="success" variant="outlined">El cliente no tiene deudas pendientes.</Alert>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Período</TableCell>
                            <TableCell>Concepto</TableCell>
                            <TableCell align="right">Total</TableCell>
                            <TableCell align="right">Saldo</TableCell>
                            <TableCell>Vencimiento</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(financiero.deudas || []).map((d, idx) => (
                            <TableRow key={d.idValorGenerado || idx} hover>
                              <TableCell>{d.idMes && d.anio ? `${d.idMes}/${d.anio}` : '-'}</TableCell>
                              <TableCell>{d.observaciones || '-'}</TableCell>
                              <TableCell align="right">${d.totalAPagar ?? '-'}</TableCell>
                              <TableCell align="right" sx={{ color: 'error.main', fontWeight: 600 }}>
                                ${d.valorSaldo ?? '-'}
                              </TableCell>
                              <TableCell>{d.fechaVencimiento || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Paper>

                {/* Fibra Points - historial */}
                {financiero.fibra_points && (
                  <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                      <HistoryIcon color="primary" fontSize="small" />
                      <Typography variant="subtitle1" fontWeight={700}>
                        Fibra Points — Historial ({(financiero.fibra_points.historial || []).length})
                      </Typography>
                    </Stack>
                    {(financiero.fibra_points.historial || []).length === 0 ? (
                      <Alert severity="info" variant="outlined">Sin canjes registrados.</Alert>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Causal</TableCell>
                              <TableCell>Ticket</TableCell>
                              <TableCell align="right">Canjeados</TableCell>
                              <TableCell align="right">Antes</TableCell>
                              <TableCell align="right">Después</TableCell>
                              <TableCell align="right">Valor</TableCell>
                              <TableCell>Estado</TableCell>
                              <TableCell>Fecha</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(financiero.fibra_points.historial || []).map((h, idx) => (
                              <TableRow key={`${h.id ?? idx}`} hover>
                                <TableCell>{h.nombre_causal || '-'}</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace' }}>{h.idTicket || '-'}</TableCell>
                                <TableCell align="right">{h.puntos_canjeados ?? '-'}</TableCell>
                                <TableCell align="right">{h.puntos_antes_canje ?? '-'}</TableCell>
                                <TableCell align="right">{h.puntos_despues_canje ?? '-'}</TableCell>
                                <TableCell align="right">${h.valor_real ?? '-'}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={h.status || '-'}
                                    size="small"
                                    color={h.status === 'PROCESADO' ? 'success' : 'default'}
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell>{h.created_at || '-'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Paper>
                )}

                {/* Servicios adicionales (TVMAX, etc.) */}
                {financiero.servicios_adicionales && Object.keys(financiero.servicios_adicionales).length > 0 && (
                  <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                      <StorefrontIcon color="primary" fontSize="small" />
                      <Typography variant="subtitle1" fontWeight={700}>Servicios adicionales</Typography>
                    </Stack>
                    <Grid container spacing={2}>
                      {Object.entries(financiero.servicios_adicionales).map(([nombre, info]) => (
                        <Grid item xs={12} sm={6} md={4} key={nombre}>
                          <Card variant="outlined">
                            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                              <Typography variant="subtitle2" fontWeight={700} color="primary.main">
                                {nombre}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {info?.tipo_plan || '-'}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                )}
              </Stack>
            )}

            {/* Datos de Aprovisionamiento */}
            {activeSection === 'aprovisionamiento' && (
              <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <SettingsInputAntennaIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle1" fontWeight={700}>
                    Datos de Aprovisionamiento ({aprovDelContrato.length})
                  </Typography>
                </Stack>

                {aprovDelContrato.length === 0 ? (
                  <Alert severity="info">Este contrato no tiene datos de aprovisionamiento registrados.</Alert>
                ) : (
                  <Stack spacing={2}>
                    {aprovDelContrato.map((a) => {
                      const campos: { label: string; value: React.ReactNode }[] = [
                        { label: 'Serial ONT', value: a.serial_ont },
                        { label: 'MAC ONT', value: a.mac_ont },
                        { label: 'OLT', value: a.nombre_olt },
                        { label: 'IP OLT', value: a.ip_olt },
                        { label: 'Service Profile', value: a.service_profile },
                        { label: 'Line Profile', value: a.line_profile },
                        { label: 'Traffic Table', value: a.traffic_table },
                        { label: 'GEM Port', value: a.gem_port },
                        { label: 'VLAN', value: a.vlan },
                        { label: 'Estado Servicio', value: a.estado_servicio },
                        { label: 'Tipo Negocio', value: a.tipo_negocio_actual },
                        { label: 'Capacidad Up', value: a.capacidad_up },
                        { label: 'Capacidad Down', value: a.capacidad_down },
                        { label: 'Usuario', value: a.usuario_acceso },
                        { label: 'Clave', value: a.clave_acceso },
                        { label: 'SSID', value: a.ssid },
                        { label: 'ONT ID', value: a.ont_id },
                        { label: 'Service Port (custom)', value: a.service_port_id_custom },
                        { label: 'Service Port (TR069)', value: a.service_port_id_tr069 },
                        { label: 'Puerto PON', value: a.puerto_pon },
                      ];
                      return (
                        <Card key={a.id} variant="outlined">
                          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                              <Chip
                                label={a.bloqueado ? 'Cancelado — equipos retirados' : 'Activo'}
                                size="small"
                                variant="outlined"
                                color={a.bloqueado ? 'error' : 'success'}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {a.fecha_modificacion
                                  ? `Actualizado: ${new Date(a.fecha_modificacion).toLocaleString('es-EC')}`
                                  : a.fecha_creacion
                                    ? `Creado: ${new Date(a.fecha_creacion).toLocaleString('es-EC')}`
                                    : ''}
                              </Typography>
                            </Stack>

                            <Grid container spacing={1}>
                              {campos.map((c) => (
                                <Grid item xs={6} sm={4} md={3} key={c.label}>
                                  <Typography variant="caption" color="text.secondary">{c.label}</Typography>
                                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                    {c.value === null || c.value === undefined || c.value === '' ? 'N/A' : c.value}
                                  </Typography>
                                </Grid>
                              ))}
                            </Grid>

                            {(a.us_creacion || a.ip_creacion) && (
                              <>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="caption" color="text.secondary">
                                  Origen: {a.us_creacion || '—'}{a.ip_creacion ? ` (${a.ip_creacion})` : ''}
                                </Typography>
                              </>
                            )}

                            {/* Acciones: solo si esta cancelado (aviso) o si el usuario
                                tiene el permiso de gestionar (cancelar/desconfigurar). */}
                            {(a.bloqueado || puedeGestionarAprov) && (
                              <>
                                <Divider sx={{ my: 1 }} />
                                {a.bloqueado ? (
                                  <Typography variant="caption" color="error.main">
                                    Servicio cancelado. El contrato no puede volver a aprovisionarse.
                                  </Typography>
                                ) : (
                                  <Stack direction="row" spacing={1}>
                                    <Button
                                      size="small"
                                      color="error"
                                      variant="outlined"
                                      disabled={ejecutandoBaja}
                                      onClick={() => setAccionBaja({ modo: 'cancelar', aprov: a })}
                                    >
                                      Cancelar
                                    </Button>
                                    <Button
                                      size="small"
                                      color="warning"
                                      variant="outlined"
                                      disabled={ejecutandoBaja}
                                      onClick={() => setAccionBaja({ modo: 'desconfigurar', aprov: a })}
                                    >
                                      Desconfigurar
                                    </Button>
                                  </Stack>
                                )}
                              </>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Stack>
                )}
              </Paper>
            )}

            {/* Trazabilidad */}
            {activeSection === 'trazabilidad' && (
              <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <HistoryIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle1" fontWeight={700}>Trazabilidad ({trazabilidad.length})</Typography>
                </Stack>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Contrato</TableCell>
                        <TableCell>Campo</TableCell>
                        <TableCell>Anterior</TableCell>
                        <TableCell>Nuevo</TableCell>
                        <TableCell>Tipo</TableCell>
                        <TableCell>Usuario</TableCell>
                        <TableCell>Fecha</TableCell>
                        <TableCell align="center">Detalle</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {trazabilidad.map((t, idx) => {
                        const detalle = getDetalleRows(t);
                        return (
                          <TableRow key={`${t.fecha}-${idx}`} hover>
                            <TableCell>
                              <Chip label={contratoDeTraza(t)} size="small" variant="outlined" color="primary" />
                            </TableCell>
                            <TableCell>{t.campo_modificado}</TableCell>
                            <TableCell sx={{ color: 'error.main' }}>{t.valor_anterior}</TableCell>
                            <TableCell sx={{ color: 'success.main' }}>{t.valor_nuevo}</TableCell>
                            <TableCell>
                              <Chip
                                label={labelTipoCambio(t.tipo_cambio)}
                                size="small"
                                variant="outlined"
                                color={colorTipoCambio(t.tipo_cambio)}
                              />
                            </TableCell>
                            <TableCell>{t.usuario_nombre}</TableCell>
                            <TableCell>{t.fecha ? new Date(t.fecha).toLocaleString('es-EC') : '—'}</TableCell>
                            <TableCell align="center">
                              {detalle.length > 0 ? (
                                <Tooltip title="Ver detalle completo de campos">
                                  <Button
                                    size="small"
                                    variant="text"
                                    startIcon={<InfoOutlinedIcon fontSize="small" />}
                                    onClick={() => setDetalleTraza(t)}
                                  >
                                    Más detalles
                                  </Button>
                                </Tooltip>
                              ) : (
                                '—'
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {trazabilidad.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={8} align="center">
                            <Typography color="text.secondary">Sin cambios registrados</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {/* Ver más Contratos */}
            {activeSection === 'contratos' && (
              <Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
                  Otros Contratos ({otrosContratos.length})
                </Typography>
                {otrosContratos.length === 0 ? (
                  <Alert severity="info">No hay otros contratos disponibles</Alert>
                ) : (
                  <Stack spacing={1.5}>
                    {otrosContratos.map((c) => (
                      <Card
                        key={c.id_contrato}
                        variant="outlined"
                        sx={{
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                        }}
                        onClick={() => {
                          onSelectContrato(c);
                          setActiveSection('datos_comerciales');
                        }}
                      >
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle2" fontWeight={700}>
                              {c.codigo}
                            </Typography>
                            <Chip
                              label={c.estado_servicio || c.estado_contrato}
                              size="small"
                              color={estadoServicioColor(c.estado_servicio || c.estado_contrato)}
                            />
                          </Stack>
                          <Divider sx={{ my: 0.5 }} />
                          <Grid container spacing={0.5}>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Plan</Typography>
                              <Typography variant="body2">{c.plan} ({c.velocidad} Mbps)</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Tipo</Typography>
                              <Typography variant="body2">{c.tipo_plan} / {c.tipo_servicio}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">IP</Typography>
                              {c.ipv4_actual ? (
                                <Typography
                                  variant="body2"
                                  component="a"
                                  href={`http://${c.ipv4_actual}:80`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{ display: 'block', color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                >
                                  {c.ipv4_actual}
                                </Typography>
                              ) : (
                                <Typography variant="body2">N/A</Typography>
                              )}
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Valor</Typography>
                              <Typography variant="body2">${c.valor_plan}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="caption" color="text.secondary">Dirección</Typography>
                              <Typography variant="body2" noWrap>{c.direccion}</Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                )}
              </Box>
            )}


            {
              activeSection === 'recepcion_equipos' && selectedContrato && (
                <TabRecepcionEquipos
                  contrato={selectedContrato}
                  cliente={cliente}
                  can={can}
                  estadoServicioColor={estadoServicioColor}
                />
              )
            }

            {
              activeSection === 'orden_servicio' && (
                <TabOrdenServicio
                  contrato={selectedContrato as unknown as Contrato}
                  cliente={cliente}
                  saldoPendiente={financiero.total_pendiente}
                  fibraPoints={financiero.fibra_points?.puntos_disponibles}
                />
              )
            }

            {/* Fallback si no hay contrato seleccionado */}
            {!selectedContrato && activeSection !== 'contratos' && activeSection !== 'masivos' && (
              <Alert severity="info">Seleccione un contrato para ver los detalles</Alert>
            )}
          </Box>
        </Grid>
      </Grid>

      <TicketDetailModal
        ticketId={openTicketModal}
        open={!!openTicketModal}
        onClose={() => setOpenTicketModal(null)}
      />

      {/* Modal "Mas detalles" de un evento de trazabilidad (desglose campo-a-campo) */}
      <Dialog open={!!detalleTraza} onClose={() => setDetalleTraza(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Detalle del cambio
          {detalleTraza?.contrato_codigo ? ` — ${detalleTraza.contrato_codigo}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap">
            <Chip
              size="small"
              label={labelTipoCambio(detalleTraza?.tipo_cambio)}
              color={colorTipoCambio(detalleTraza?.tipo_cambio)}
              variant="outlined"
            />
            {detalleTraza?.usuario_nombre && (
              <Chip size="small" label={detalleTraza.usuario_nombre} variant="outlined" />
            )}
            {detalleTraza?.fecha && (
              <Chip size="small" label={new Date(detalleTraza.fecha).toLocaleString('es-EC')} variant="outlined" />
            )}
          </Stack>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Campo</TableCell>
                  <TableCell>Anterior</TableCell>
                  <TableCell>Nuevo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(detalleTraza ? getDetalleRows(detalleTraza) : []).map((d, i) => (
                  <TableRow key={`${d.campo}-${i}`} hover>
                    <TableCell>{d.campo}</TableCell>
                    <TableCell sx={{ color: 'error.main' }}>{d.anterior ?? '—'}</TableCell>
                    <TableCell sx={{ color: 'success.main' }}>{d.nuevo ?? '—'}</TableCell>
                  </TableRow>
                ))}
                {(detalleTraza ? getDetalleRows(detalleTraza) : []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography color="text.secondary">Sin desglose disponible</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetalleTraza(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Confirmacion de Cancelar / Desconfigurar aprovisionamiento */}
      <Dialog open={!!accionBaja} onClose={() => !ejecutandoBaja && setAccionBaja(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {accionBaja?.modo === 'cancelar' ? 'Cancelar servicio' : 'Desconfigurar aprovisionamiento'}
          {accionBaja?.aprov.contrato_codigo ? ` — ${accionBaja.aprov.contrato_codigo}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {accionBaja?.modo === 'cancelar' ? (
            <Typography variant="body2">
              Se cancelará el servicio del contrato y los equipos quedarán como
              <strong> retirados</strong>. Esta acción <strong>no se puede revertir</strong>:
              el contrato no podrá volver a aprovisionarse. ¿Continuar?
            </Typography>
          ) : (
            <Typography variant="body2">
              Se borrará la configuración de aprovisionamiento de este contrato. El contrato
              <strong> sí podrá volver a aprovisionarse</strong> más adelante. ¿Continuar?
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccionBaja(null)} disabled={ejecutandoBaja}>Cancelar</Button>
          <Button
            variant="contained"
            color={accionBaja?.modo === 'cancelar' ? 'error' : 'warning'}
            disabled={ejecutandoBaja}
            onClick={confirmarBaja}
          >
            {ejecutandoBaja ? 'Procesando…' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
